import { MoreHorizontal, Plus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CopyButton } from '@/components/common/CopyButton';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { useToast } from '@/components/shared/Toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { mutationErrorMessage } from '@/features/common/apiError';
import { PERMISSION_ACTIONS, orgResource } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { useGetRolesQuery } from '@/features/roles/api/rolesApi';
import { AddMemberDialog } from '../components/AddMemberDialog';
import { AssignRolesDialog } from '../components/AssignRolesDialog';
import { OrgRoleBadge } from '../components/OrgRoleBadge';
import { useGetOrganizationFullQuery, useRemoveMemberMutation } from '../api/orgApi';
import type { OrgMember } from '../types';

/** Design §14.5: plain-language rationale, never a raw action string (D5). */
const REMOVE_DENIED_TOOLTIP =
  "You don't have permission to remove members. Ask an organization owner or admin.";

/**
 * Members tab (`/organizations/:orgId/members`, design §10).
 *
 * Data: `GET /organizations/:id/full` — the members live there, not on the plain
 * detail document (design conflict A1, resolved in `api/orgApi.ts`).
 *
 * Gating: “Add member” is **hidden** while “Remove member” is **disabled with a
 * tooltip** — §14.5's two treatments. Both read one verdict each
 * (`organizations:member:add|remove` on `org:${orgId}`), evaluated once per page
 * and shared by every row (§14.3). `allowed === undefined` (unresolved) renders
 * hidden/disabled-without-tooltip: no flash of an enabled control.
 */
export function OrgMembersPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const toast = useToast();

  const orgQuery = useGetOrganizationFullQuery(orgId);
  const { data: roles = [], isLoading: rolesLoading } = useGetRolesQuery();
  const [removeMember, removeState] = useRemoveMemberMutation();

  const canAdd = useCan(PERMISSION_ACTIONS.memberAdd, orgResource(orgId));
  const canRemove = useCan(PERMISSION_ACTIONS.memberRemove, orgResource(orgId));

  const [addOpen, setAddOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<OrgMember | null>(null);
  /**
   * Role ids applied during this session. `OrganizationMember.roleIds` is written
   * by `POST .../members` only — `/roles/:id/assign` records the assignment in the
   * roles service and never writes back — so the Roles column shows the applied
   * set once the dialog changed it instead of a stale document field.
   */
  const [appliedRoleIds, setAppliedRoleIds] = useState<Record<string, string[]>>({});

  const orgName = orgQuery.data?.organization?.name ?? '';
  const members = useMemo<OrgMember[]>(
    () =>
      (orgQuery.data?.members ?? []).map((member) => ({
        ...member,
        roleIds: appliedRoleIds[member.userId] ?? member.roleIds,
      })),
    [orgQuery.data, appliedRoleIds],
  );
  const roleNameById = useMemo(() => new Map(roles.map((role) => [role._id, role.name])), [roles]);
  const assignMember = assignUserId
    ? (members.find((member) => member.userId === assignUserId) ?? null)
    : null;

  const addButton = (
    <Button onClick={() => setAddOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      Add member
    </Button>
  );
  // Hide-mode through `<Can>` for the page-level entry point (§14.2); the empty
  // state needs the boolean because it must receive `undefined` when denied.
  const addMemberAction = canAdd.allowed === true ? addButton : undefined;

  const columns: DataTableColumn<OrgMember>[] = useMemo(
    () => [
      {
        id: 'member',
        header: 'Member',
        cell: (member) => (
          <div className="flex items-center gap-1">
            <span className="font-mono text-xs">{member.userId}</span>
            <CopyButton value={member.userId} label="Copy user ID" />
          </div>
        ),
      },
      {
        id: 'orgRole',
        header: 'Organization role',
        cell: (member) => <OrgRoleBadge role={member.orgRole} />,
      },
      {
        id: 'roles',
        header: 'Roles',
        // Read-only here: the × unassign affordance lives in the dialog (F2).
        cell: (member) => {
          const roleIds = member.roleIds ?? [];
          if (roleIds.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {roleIds.map((roleId) => (
                <Badge key={roleId} variant="outline">
                  {roleNameById.get(roleId) ?? roleId}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        srHeader: 'Actions',
        className: 'text-right',
        headerClassName: 'text-right',
        cell: (member) => {
          const denied = canRemove.allowed === false;
          const disabled = canRemove.allowed !== true;

          const removeItem = (
            <DropdownMenuItem
              disabled={disabled}
              className="text-destructive focus:text-destructive"
              onSelect={() => {
                if (!disabled) {
                  setRemoveTarget(member);
                }
              }}
            >
              Remove member…
            </DropdownMenuItem>
          );

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setAssignUserId(member.userId)}>
                  Assign roles…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {denied ? (
                  // The item itself is `pointer-events-none` when disabled, so the
                  // tooltip trigger wraps it in a span that still receives hover.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">{removeItem}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{REMOVE_DENIED_TOOLTIP}</TooltipContent>
                  </Tooltip>
                ) : (
                  removeItem
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [canRemove.allowed, roleNameById],
  );

  return (
    <section aria-labelledby="members-title">
      <PageHeader
        title="Members"
        titleAs="h2"
        titleId="members-title"
        actions={
          <Can action={PERMISSION_ACTIONS.memberAdd} resource={orgResource(orgId)}>
            {addButton}
          </Can>
        }
      />

      {orgQuery.isLoading ? (
        <LoadingRows label="Loading members…" columns={4} />
      ) : orgQuery.isError ? (
        <QueryErrorView
          error={orgQuery.error}
          label="members"
          onRetry={() => void orgQuery.refetch()}
        />
      ) : (
        <DataTable
          columns={columns}
          data={members}
          pageSize={10}
          getRowId={(member) => member.userId}
          emptyState={
            <EmptyState
              icon={<Users />}
              title="No members"
              description="Add someone to start collaborating."
              action={addMemberAction}
            />
          }
        />
      )}

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} orgId={orgId} />

      {assignMember ? (
        <AssignRolesDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setAssignUserId(null);
            }
          }}
          orgId={orgId}
          member={assignMember}
          roles={roles}
          rolesLoading={rolesLoading}
          onAppliedRoleIds={(userId, roleIds) =>
            setAppliedRoleIds((current) => ({ ...current, [userId]: roleIds }))
          }
        />
      ) : null}

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRemoveTarget(null);
          }
        }}
        title="Remove member?"
        description={`This revokes their access to ${orgName} immediately. They can be added back later.`}
        confirmLabel={removeState.isLoading ? 'Removing…' : 'Remove member'}
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={removeState.isLoading}
        onConfirm={async () => {
          if (!removeTarget) {
            return;
          }
          try {
            await removeMember({ orgId, userId: removeTarget.userId }).unwrap();
            toast.success('Member removed.');
            setRemoveTarget(null);
          } catch (error) {
            toast.error(mutationErrorMessage(error, "Couldn't remove member."));
          }
        }}
      />
    </section>
  );
}
